import type { MachineSupaDto, VehicleStatusResponse } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { useContext } from "react"
import {
  Navigate,
  useLoaderData,
  useParams,
  type LoaderFunction,
} from "react-router-dom"
import { getVehicleStatus } from "../../api/VehiclesApi"
import { isDev, isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useActiveOrgId } from "../../hooks"
import { ErrorBanner } from "../MessageBanner"
import VehiclePageLayout from "../VehiclePageLayout"
import { DieselPrimusDashboard } from "./DieselPrimusDashboard"
import { EVertiFeedDashboard } from "./EVertiFeedDashboard"

function VehicleOverview() {
  const params = useParams<{ vin: string }>()
  const { userData } = useContext(UserDataContext)
  const activeOrgId = useActiveOrgId()

  let machine: MachineSupaDto | undefined
  if (activeOrgId && userData && params.vin) {
    const org = UserDataContextHelper.findOrg(activeOrgId, userData)
    const machinesFromUserCtx = org.Machines
    machine = machinesFromUserCtx.filter(
      (m) => m.serial_number === params.vin
    )[0]
  }
  // console.debug("machine =", machine)

  const vehicleStatus = useLoaderData() as VehicleStatusResponse
  // !isProd && console.debug("🪅 VehicleOverview: vehicleStatus =", vehicleStatus)

  if (!vehicleStatus || vehicleStatus.Telemetry === null) {
    return (
      <VehiclePageLayout vin={params.vin!}>
        <ErrorBanner
          error={I18n.get("Fehler beim Laden der Daten: Keine Daten gefunden")}
        />
      </VehiclePageLayout>
    )
  }

  const machineTypeLc = machine?.machine_type?.toLowerCase()
  let dashboard
  if (machineTypeLc === "evertifeed") {
    dashboard = <EVertiFeedDashboard vehicleStatus={vehicleStatus} />
  } else if (machineTypeLc?.startsWith("p")) {
    dashboard = <DieselPrimusDashboard vehicleStatus={vehicleStatus} />
  } else {
    console.warn(
      `Es gibt kein Dashboard für den Maschinentyp '${machineTypeLc}'.`
    )
    !isProd &&
      console.debug("VehicleOverview: no matching dashboard.", {
        machine,
        "machine.id": machine?.id,
        "machine.machine_type": machine?.machine_type,
      })
    return <Navigate to="./debug/" replace />
  }

  return (
    <VehiclePageLayout vin={params.vin!}>
      {dashboard}
      {isDev && <div>[DEV] machine_type: {machine?.machine_type}</div>}
    </VehiclePageLayout>
  )
}

export const loader: LoaderFunction = async ({
  params,
}): Promise<VehicleStatusResponse> => {
  const vin = params["vin"]
  if (!vin) {
    throw new Error("No VIN given")
  }
  const data = await getVehicleStatus(vin)
  if (data === false) {
    throw new Error("Auth error")
  }
  return data
}

export const Component = VehicleOverview
